import { useState, useEffect, useRef } from 'react'
import { ChatBubble } from '../ChatBubble/ChatBubble'
import { InspirationalQuote } from '../InspirationalQuote/InspirationalQuote'
import type { LandingFeature } from './features'

interface ParticlePosition {
  x: number
  y: number
  color: string
}

interface FeatureBubblesProps {
  features: LandingFeature[]
  isActive?: boolean
}

function generateParticles(): ParticlePosition[] {
  const screenWidth = window.innerWidth
  const screenHeight = window.innerHeight

  return [
    { x: screenWidth * 0.15, y: screenHeight * 0.25, color: '#4A90E2' },
    { x: screenWidth * 0.85, y: screenHeight * 0.30, color: '#7B68EE' },
    { x: screenWidth * 0.20, y: screenHeight * 0.70, color: '#20B2AA' },
    { x: screenWidth * 0.80, y: screenHeight * 0.65, color: '#FF6B6B' },
    { x: screenWidth * 0.50, y: screenHeight * 0.45, color: '#4ECDC4' },
    { x: screenWidth * 0.25, y: screenHeight * 0.40, color: '#45B7D1' },
    { x: screenWidth * 0.75, y: screenHeight * 0.50, color: '#4A90E2' },
    { x: screenWidth * 0.30, y: screenHeight * 0.60, color: '#7B68EE' },
  ]
}

function getResponsiveBubblePosition(
  currentSide: 'left' | 'right',
  featureIndex: number,
): { x: number; y: number } {
  const screenWidth = window.innerWidth
  const screenHeight = window.innerHeight
  const isMobile = screenWidth <= 768
  const isTablet = screenWidth > 768 && screenWidth <= 1024

  if (isMobile) {
    return {
      x: screenWidth * 0.02,
      y: screenHeight * 0.25 + (featureIndex % 4) * 80,
    }
  }

  if (isTablet) {
    return {
      x: currentSide === 'left' ? screenWidth * 0.03 : screenWidth * 0.55,
      y: screenHeight * 0.28 + (featureIndex % 3) * 90,
    }
  }

  return {
    x: currentSide === 'left' ? screenWidth * 0.05 : screenWidth * 0.60,
    y: screenHeight * 0.30 + (featureIndex % 3) * 100,
  }
}

export function FeatureBubbles({ features, isActive = true }: FeatureBubblesProps) {
  const [currentFeatureIndex, setCurrentFeatureIndex] = useState(0)
  const [isVisible, setIsVisible] = useState(false)
  const [showQuote, setShowQuote] = useState(false)
  const [currentQuoteIndex, setCurrentQuoteIndex] = useState(0)
  const [particles, setParticles] = useState<ParticlePosition[]>([])
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const quoteIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const currentIndexRef = useRef(0)

  useEffect(() => {
    currentIndexRef.current = currentFeatureIndex
  }, [currentFeatureIndex])

  useEffect(() => {
    setParticles(generateParticles())
    const handleResize = () => setParticles(generateParticles())
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    if (!isActive || features.length === 0) {
      setIsVisible(false)
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      return
    }

    const startTimer = setTimeout(() => setIsVisible(true), 1000)

    intervalRef.current = setInterval(() => {
      setIsVisible(false)
      setTimeout(() => {
        const nextIndex = (currentIndexRef.current + 1) % features.length
        currentIndexRef.current = nextIndex
        setCurrentFeatureIndex(nextIndex)
        setTimeout(() => setIsVisible(true), 200)
      }, 2200)
    }, 6000)

    return () => {
      clearTimeout(startTimer)
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [isActive, features.length])

  useEffect(() => {
    if (!isActive) {
      setShowQuote(false)
      if (quoteIntervalRef.current) {
        clearInterval(quoteIntervalRef.current)
        quoteIntervalRef.current = null
      }
      return
    }

    const startTimer = setTimeout(() => {
      setShowQuote(true)
      quoteIntervalRef.current = setInterval(() => {
        setShowQuote(false)
        setTimeout(() => {
          setCurrentQuoteIndex((prev) => (prev + 1) % 3)
          setShowQuote(true)
        }, 2200)
      }, 8000)
    }, 3000)

    return () => {
      clearTimeout(startTimer)
      if (quoteIntervalRef.current) {
        clearInterval(quoteIntervalRef.current)
        quoteIntervalRef.current = null
      }
    }
  }, [isActive])

  if (!isActive || features.length === 0 || particles.length === 0) {
    return null
  }

  const currentFeature = features[currentFeatureIndex]
  const currentSide: 'left' | 'right' = currentFeatureIndex % 2 === 0 ? 'left' : 'right'
  const targetParticle = particles[currentFeatureIndex % particles.length]
  const bubblePosition = getResponsiveBubblePosition(currentSide, currentFeatureIndex)

  return (
    <>
      <ChatBubble
        title={currentFeature.title}
        description={currentFeature.description}
        isVisible={isVisible}
        side={currentSide}
        borderColor={currentFeature.color}
        targetPosition={targetParticle}
        bubblePosition={bubblePosition}
      />
      <InspirationalQuote isVisible={showQuote} currentQuoteIndex={currentQuoteIndex} />
    </>
  )
}
